import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RefreshCw, FileText, Clock, AlertTriangle, Building2, Info, Layers, Code } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import api from '../lib/api';

const TIME_RANGES = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'Last 1 Week' },
  { value: '30', label: 'Last 1 Month' },
  { value: '90', label: 'Last 3 Months' },
];

const ANCHOR_PARTNER_ID = '14';

const SOURCE_COLORS = {
  Email: 'bg-blue-100 text-blue-800 border-blue-200',
  Portal: 'bg-green-100 text-green-800 border-green-200',
  'Manual Upload': 'bg-amber-100 text-amber-800 border-amber-200',
  Other: 'bg-gray-100 text-gray-800 border-gray-200',
};

const STATUS_COLORS = {
  AutoParsed: 'bg-emerald-100 text-emerald-800',
  ManualParsed: 'bg-sky-100 text-sky-800',
  ManualQueue: 'bg-orange-100 text-orange-800',
  DevParser: 'bg-purple-100 text-purple-800',
  Unparsed: 'bg-red-100 text-red-800',
  Other: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS = {
  AutoParsed: 'Auto Parsed',
  ManualParsed: 'Manual Parsed',
  ManualQueue: 'Manual Queue',
  DevParser: 'Dev Parser',
  Unparsed: 'Unparsed',
  Other: 'Other',
};

export default function BillStreamReport() {
  const [days, setDays] = useState('7');
  const [partnerId, setPartnerId] = useState(ANCHOR_PARTNER_ID);
  const [partners, setPartners] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch partners list once on mount
  useEffect(() => {
    api.get('/payment-verification/v2/reports/partners')
      .then(({ data: result }) => setPartners(result.partners || []))
      .catch(() => console.error('Failed to load partners'));
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (partnerId && partnerId !== 'all') {
        params.append('partner_id', partnerId);
      }
      const { data: result } = await api.get(`/payment-verification/v2/reports/bill-stream?${params.toString()}`);
      setData(result);
    } catch {
      console.error('Failed to load bill stream report');
    } finally {
      setLoading(false);
    }
  }, [days, partnerId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const { bill_stream, unparsed_aging, manual_queue_aging, devparser_aging } = data;

  return (
    <div className="space-y-6" data-testid="bill-stream-report">
      {/* Header with partner filter */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Bill Stream Report</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Bills received, parsing status, and aging queues by customer
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Partner Filter */}
          <Select value={partnerId} onValueChange={setPartnerId}>
            <SelectTrigger className="w-56 rounded-none h-9" data-testid="bill-stream-partner-filter">
              <Building2 className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Select Partner" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="rounded-none">All Partners</SelectItem>
              {partners.map(p => (
                <SelectItem key={p.id} value={String(p.id)} className="rounded-none">
                  {p.name} ({p.customer_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="inline-flex items-center justify-center h-9 w-9 border border-border rounded-none hover:bg-muted transition-colors disabled:opacity-50"
            data-testid="bill-stream-refresh"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Queue Summary Card: Unparsed + Manual Queue + DevParser by Customer */}
      <QueueSummaryCard unparsed={unparsed_aging} manualQueue={manual_queue_aging} devParser={devparser_aging} />

      {/* Card 2: Bill Stream by Customer */}
      <div className="border border-border rounded-none bg-card" data-testid="bill-stream-card">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-sm">Bills Received by Customer</h3>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-40 rounded-none h-8 ml-2 text-xs" data-testid="bill-stream-time-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {TIME_RANGES.map(r => (
                    <SelectItem key={r.value} value={r.value} className="rounded-none">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5" data-testid="total-bills-received">
                <span className="text-xs text-muted-foreground">Total Received</span>
                <span className="text-sm font-bold">{bill_stream.totals.total.toLocaleString()}</span>
              </div>
              <div className="h-4 w-px bg-border" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help" data-testid="total-unprocessed">
                      <span className="text-xs text-muted-foreground">Total Unprocessed</span>
                      <span className="text-sm font-bold text-orange-600">
                        {((bill_stream.totals.by_status.Unparsed || 0) + (bill_stream.totals.by_status.ManualQueue || 0) + (bill_stream.totals.by_status.DevParser || 0)).toLocaleString()}
                      </span>
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="rounded-none text-xs">
                    <p>Unparsed: {(bill_stream.totals.by_status.Unparsed || 0).toLocaleString()}</p>
                    <p>Manual Queue: {(bill_stream.totals.by_status.ManualQueue || 0).toLocaleString()}</p>
                    <p>Dev Parser: {(bill_stream.totals.by_status.DevParser || 0).toLocaleString()}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          {/* Source breakdown badges */}
          <div className="flex items-center gap-3 mt-3">
            {Object.entries(bill_stream.totals.by_source).map(([src, cnt]) => cnt > 0 && (
              <Badge key={src} variant="outline" className={`rounded-none text-xs ${SOURCE_COLORS[src] || ''}`}>
                {src}: {cnt.toLocaleString()}
              </Badge>
            ))}
          </div>
          {/* Status summary bar */}
          <div className="flex items-center gap-3 mt-2">
            {Object.entries(bill_stream.totals.by_status).map(([status, cnt]) => cnt > 0 && (
              <span key={status} className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm ${STATUS_COLORS[status] || ''}`}>
                {STATUS_LABELS[status] || status}: {cnt.toLocaleString()}
              </span>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">#</th>
                <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Customer</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Portal</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Upload</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground border-l border-border">Auto Parsed</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Manual Parsed</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Manual Queue</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Dev Parser</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Unparsed</th>
                <th className="px-4 py-2.5 text-right font-medium text-muted-foreground border-l border-border">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {bill_stream.customers.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-muted-foreground">No bills found for the selected filters</td>
                </tr>
              ) : (
                bill_stream.customers.map((c, i) => (
                  <tr key={c.customer} className="hover:bg-muted/30" data-testid={`bill-stream-row-${i}`}>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-2 font-medium">{c.customer}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.by_source.Email || '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.by_source.Portal || '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.by_source['Manual Upload'] || '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums border-l border-border">{c.by_status.AutoParsed || '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.by_status.ManualParsed || '-'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.by_status.ManualQueue > 0 ? (
                        <span className="text-orange-600 font-medium">{c.by_status.ManualQueue}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.by_status.DevParser > 0 ? (
                        <span className="text-purple-600 font-medium">{c.by_status.DevParser}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.by_status.Unparsed > 0 ? (
                        <span className="text-red-600 font-medium">{c.by_status.Unparsed}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold border-l border-border">{c.total}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aging Queues — Tabbed Full Width Card */}
      <AgingTabsCard
        unparsed={unparsed_aging}
        manualQueue={manual_queue_aging}
        devParser={devparser_aging}
      />
    </div>
  );
}

function QueueSummaryCard({ unparsed, manualQueue, devParser }) {
  // Merge unparsed, manual queue, and devparser customers into combined rows
  const customerMap = {};
  for (const c of unparsed.customers) {
    customerMap[c.customer] = { customer: c.customer, unparsed: c.total, manual_queue: 0, dev_parser: 0, total: c.total };
  }
  for (const c of manualQueue.customers) {
    if (customerMap[c.customer]) {
      customerMap[c.customer].manual_queue = c.total;
      customerMap[c.customer].total += c.total;
    } else {
      customerMap[c.customer] = { customer: c.customer, unparsed: 0, manual_queue: c.total, dev_parser: 0, total: c.total };
    }
  }
  for (const c of devParser.customers) {
    if (customerMap[c.customer]) {
      customerMap[c.customer].dev_parser = c.total;
      customerMap[c.customer].total += c.total;
    } else {
      customerMap[c.customer] = { customer: c.customer, unparsed: 0, manual_queue: 0, dev_parser: c.total, total: c.total };
    }
  }
  const rows = Object.values(customerMap).sort((a, b) => b.total - a.total);
  const grandUnparsed = unparsed.totals.total;
  const grandManual = manualQueue.totals.total;
  const grandDev = devParser.totals.total;
  const grandTotal = grandUnparsed + grandManual + grandDev;

  return (
    <div className="border border-border rounded-none bg-card" data-testid="queue-summary-card">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-orange-600" />
            <h3 className="font-semibold text-sm">Unprocessed Queue by Customer</h3>
          </div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm bg-red-100 text-red-800">
              Unparsed: {grandUnparsed.toLocaleString()}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm bg-orange-100 text-orange-800">
              Manual Queue: {grandManual.toLocaleString()}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm bg-purple-100 text-purple-800">
              Dev Parser: {grandDev.toLocaleString()}
            </span>
            <span className="text-sm font-bold text-orange-700">{grandTotal.toLocaleString()} total</span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">#</th>
              <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Customer</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Unparsed</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Manual Queue</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Dev Parser</th>
              <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Queue is empty</td>
              </tr>
            ) : (
              rows.map((c, i) => (
                <tr key={c.customer} className="hover:bg-muted/30" data-testid={`queue-row-${i}`}>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2 font-medium">{c.customer}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {c.unparsed > 0 ? <span className="text-red-600 font-medium">{c.unparsed.toLocaleString()}</span> : '-'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {c.manual_queue > 0 ? <span className="text-orange-600 font-medium">{c.manual_queue.toLocaleString()}</span> : '-'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {c.dev_parser > 0 ? <span className="text-purple-600 font-medium">{c.dev_parser.toLocaleString()}</span> : '-'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{c.total.toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgingTabsCard({ unparsed, manualQueue, devParser }) {
  const tabs = [
    {
      id: 'unparsed',
      label: 'Unparsed',
      icon: <Clock className="h-4 w-4" />,
      data: unparsed,
      color: 'text-red-600',
      badgeClass: 'bg-red-100 text-red-800',
      rowColor: { '48h': 'text-yellow-600', '5d': 'text-orange-600', '2w': 'text-red-600' },
    },
    {
      id: 'manual',
      label: 'Manual Queue',
      icon: <AlertTriangle className="h-4 w-4" />,
      data: manualQueue,
      color: 'text-orange-600',
      badgeClass: 'bg-orange-100 text-orange-800',
      rowColor: { '48h': 'text-yellow-600', '5d': 'text-orange-600', '2w': 'text-red-600' },
    },
    {
      id: 'devparser',
      label: 'Dev Parser',
      icon: <Code className="h-4 w-4" />,
      data: devParser,
      color: 'text-purple-600',
      badgeClass: 'bg-purple-100 text-purple-800',
      rowColor: { '48h': 'text-purple-500', '5d': 'text-purple-600', '2w': 'text-purple-800' },
    },
  ];

  return (
    <div className="border border-border rounded-none bg-card" data-testid="aging-tabs-card">
      <Tabs defaultValue="unparsed">
        <div className="px-4 pt-3 pb-0 border-b border-border">
          <TabsList className="bg-transparent h-auto p-0 gap-0 rounded-none">
            {tabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-current data-[state=active]:shadow-none px-4 pb-2.5 pt-1 gap-2 text-sm"
                data-testid={`aging-tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
                <span className={`ml-1 inline-flex items-center px-1.5 py-0.5 text-xs font-bold rounded-sm ${tab.badgeClass}`}>
                  {tab.data.totals.total.toLocaleString()}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {tabs.map(tab => (
          <TabsContent key={tab.id} value={tab.id} className="mt-0">
            <div className="px-4 py-3 flex items-center gap-4 border-b border-border bg-muted/30">
              <Badge variant="outline" className="rounded-none text-xs bg-blue-50 border-blue-200 text-blue-800">
                &gt;24h: {tab.data.totals.over_24h}
              </Badge>
              <Badge variant="outline" className="rounded-none text-xs bg-yellow-50 border-yellow-200 text-yellow-800">
                &gt;48h: {tab.data.totals.over_48h}
              </Badge>
              <Badge variant="outline" className="rounded-none text-xs bg-orange-50 border-orange-200 text-orange-800">
                &gt;5 days: {tab.data.totals.over_5d}
              </Badge>
              <Badge variant="outline" className="rounded-none text-xs bg-red-50 border-red-200 text-red-800">
                &gt;2 weeks: {tab.data.totals.over_2w}
              </Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground w-12">#</th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Customer</th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">&gt;24h</th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">&gt;48h</th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">&gt;5 days</th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">&gt;2 weeks</th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tab.data.customers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No items in queue</td>
                    </tr>
                  ) : (
                    tab.data.customers.map((c, i) => (
                      <tr key={c.customer} className="hover:bg-muted/30">
                        <td className="px-4 py-2 text-xs text-muted-foreground">{i + 1}</td>
                        <td className="px-4 py-2 font-medium">{c.customer}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {c.over_24h > 0 ? <span className="text-blue-600 font-medium">{c.over_24h}</span> : '-'}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {c.over_48h > 0 ? <span className={`font-medium ${tab.rowColor['48h']}`}>{c.over_48h}</span> : '-'}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {c.over_5d > 0 ? <span className={`font-medium ${tab.rowColor['5d']}`}>{c.over_5d}</span> : '-'}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {c.over_2w > 0 ? <span className={`font-bold ${tab.rowColor['2w']}`}>{c.over_2w}</span> : '-'}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-bold">{c.total}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
